import React from "react";
import { Alert, Badge, Button, Divider, Group, Modal, Paper, Stack, Text } from "@mantine/core";
import { skipToken } from "@reduxjs/toolkit/query";
import { FaArrowRight, FaTrash } from "react-icons/fa6";
import { useAppDispatch, useAppSelector } from "../../../../app/hooks";
import ResultAlgosBar from "../../../ResultAlgosBar";
import {
  useGetMcbimItemsInSpaceQuery,
  useGetMcbimSpacesInProjectQuery,
  usePostMultiplacerDevicesQuery,
  usePostMultiplacerRoomsQuery,
} from "../../../../../openapi";
import {
  selectCachedMatchingSpacesForAdvancedCopy,
  selectAdvancedCopyDevicesSourceRoomInfo,
  selectGetMatchingDevicesForAdvancedCopyArgs,
  selectGetTargetMcbimItemsForAdvancedCopyArgs,
  selectGetMatchingRoomsForAdvancedCopyArgs,
  selectGetMatchingSpacesForAdvancedCopyArgs,
  selectSelectedTargetRoomCountForAdvancedCopy,
  selectSelectedTargetRoomAlreadyCopiedForAdvancedCopy,
  selectSelectedTargetMcbimSpaceForAdvancedCopy,
  selectSelectedTargetTableRowForAdvancedCopy,
  selectSelectedTargetRoomForAdvancedCopy,
} from "./advancedCopyDevicesSelectors";
import { AdvancedCopyDevicesSourceViewer } from "./AdvancedCopyDevicesSourceViewer";
import { AdvancedCopyDevicesTargetViewer } from "./AdvancedCopyDevicesTargetViewer";
import { AdvancedCopyDevicesSimilarityBadge } from "./AdvancedCopyDevicesSimilarityBadge";
import { AdvancedCopyDevicesSpacesTable } from "./AdvancedCopyDevicesSpacesTable";
import { thunkCopyDevicesToSingleSelectedTargetRoom } from "./thunkCopyDevicesToSingleSelectedTargetRoom";
import { thunkCopyDevicesToMultipleSelectedTargetRooms } from "./thunkCopyDevicesToMultipleSelectedTargetRooms";
import { thunkRemoveCopiedDevicesFromSelectedTargetRoom } from "./thunkRemoveCopiedDevicesFromSelectedTargetRoom";

type Props = {
  onClose: () => void;
};

export const AdvancedCopyDevicesModal: React.FC<Props> = ({ onClose }) => {
  const dispatch = useAppDispatch();
  const queryArgs = useAppSelector(selectGetMatchingSpacesForAdvancedCopyArgs);
  const roomsQueryArgs = useAppSelector(selectGetMatchingRoomsForAdvancedCopyArgs);
  const devicesQueryArgs = useAppSelector(selectGetMatchingDevicesForAdvancedCopyArgs);
  const targetMcbimItemsArgs = useAppSelector(selectGetTargetMcbimItemsForAdvancedCopyArgs);
  const matchingSpaces = useAppSelector(selectCachedMatchingSpacesForAdvancedCopy);
  const sourceRoomInfo = useAppSelector(selectAdvancedCopyDevicesSourceRoomInfo);
  const selectedTargetRoomAlreadyCopied = useAppSelector(
    selectSelectedTargetRoomAlreadyCopiedForAdvancedCopy
  );
  const selectedTargetRoomCount = useAppSelector(selectSelectedTargetRoomCountForAdvancedCopy);
  const selectedTargetMcbimSpace = useAppSelector(selectSelectedTargetMcbimSpaceForAdvancedCopy);
  const selectedTargetRoom = useAppSelector(selectSelectedTargetRoomForAdvancedCopy);
  const selectedTargetTableRow = useAppSelector(selectSelectedTargetTableRowForAdvancedCopy);
  const previewDeviceCount = useAppSelector(
    (state) => state.design.nonPublischedDevicesFromAlgos.length
  );
  const savingDevices = useAppSelector((state) => state.device.savingDevices);

  const { isLoading, isFetching, isError } = useGetMcbimSpacesInProjectQuery(
    queryArgs ?? skipToken
  );
  usePostMultiplacerRoomsQuery(roomsQueryArgs ?? skipToken);
  usePostMultiplacerDevicesQuery(devicesQueryArgs ?? skipToken);
  useGetMcbimItemsInSpaceQuery(targetMcbimItemsArgs ?? skipToken);

  const copyDisabled =
    !selectedTargetMcbimSpace ||
    selectedTargetTableRow?.hasExistingDevices ||
    selectedTargetRoomAlreadyCopied;

  const copyHint = !selectedTargetMcbimSpace
    ? "Velg et målrom i tabellen"
    : selectedTargetTableRow?.hasExistingDevices
      ? "Målrommet har allerede komponenter"
      : selectedTargetRoomAlreadyCopied
        ? "Målrommet er allerede kopiert i denne økten"
        : "Klar til å kopiere";

  return (
    <Modal
      opened
      onClose={onClose}
      title={<Text fw={700}>Avansert kopiering av devices</Text>}
      fullScreen
      centered
    >
      <Stack gap="md" h="100%">
        <Paper withBorder radius="md" p="md" bg="gray.0">
          <Group justify="space-between" align="flex-start" wrap="wrap" gap="md">
            <Stack gap={4}>
              <Text fw={700}>Rom med samme romnavn</Text>
              <Text size="sm" c="dimmed">
                Velg et målrom, sammenlign romformene og kopier komponentene til høyre rom.
              </Text>
            </Stack>

            <Group gap="xs">
              <Badge variant="light" color="blue" size="lg">
                Treff: {matchingSpaces.length}
              </Badge>
              {selectedTargetMcbimSpace && (
                <Badge variant="light" color="gray" size="lg">
                  Valgt målrom: {selectedTargetMcbimSpace.name}
                </Badge>
              )}
            </Group>
          </Group>
        </Paper>

        {(previewDeviceCount > 0 || savingDevices) && (
          <Paper radius="md" style={{ overflow: "hidden" }}>
            <ResultAlgosBar />
          </Paper>
        )}

        <Paper withBorder radius="md" p="sm" shadow="xs">
          <Stack gap="xs">
            <Group justify="space-between" align="center" px="xs" pt="xs">
              <Stack gap={2}>
                <Text fw={600}>Tilgjengelige målrom</Text>
                <Text size="sm" c="dimmed">
                  Sorter og filtrer for å finne riktig rom
                </Text>
              </Stack>
              {selectedTargetRoomCount > 0 && (
                <Group gap="sm">
                  <Badge variant="light" color="blue" size="lg">
                    {selectedTargetRoomCount} valgt{selectedTargetRoomCount === 1 ? "" : "e"}
                  </Badge>
                  <Button
                    onClick={() => dispatch(thunkCopyDevicesToMultipleSelectedTargetRooms())}
                    rightSection={<FaArrowRight size={14} />}
                  >
                    Kopier valgte rom
                  </Button>
                </Group>
              )}
            </Group>
            <Divider />
            <AdvancedCopyDevicesSpacesTable />
          </Stack>
        </Paper>

        <Group align="stretch" gap="md" style={{ flex: 1, minHeight: 0 }}>
          <Paper withBorder radius="lg" p="md" shadow="xs" style={{ flex: 1, minWidth: 0 }}>
            <Stack gap="xs" h="100%">
              <Group justify="space-between" align="flex-start" wrap="wrap" gap="xs">
                <Stack gap={2}>
                  <Text fw={600}>Kilderom</Text>
                  <Text size="sm" c="dimmed">
                    Rommet du kopierer fra
                  </Text>
                </Stack>
                <Badge variant="light" color="blue">
                  Kilde
                </Badge>
              </Group>
              <Group gap="xs" wrap="wrap">
                <Badge variant="light" color="gray">
                  Romnavn: {sourceRoomInfo.roomName || "Mangler romnavn"}
                </Badge>
                <Badge variant="light" color="gray">
                  Romsnummer: {sourceRoomInfo.roomNumber || "Mangler romsnummer"}
                </Badge>
                <Badge variant="light" color="gray">
                  Standardrom: {sourceRoomInfo.standardRoomNumber || "-"}
                </Badge>
              </Group>
              <AdvancedCopyDevicesSourceViewer />
            </Stack>
          </Paper>

          <Paper
            withBorder
            radius="lg"
            p="md"
            bg="blue.0"
            style={{ width: 220, flexShrink: 0, alignSelf: "center" }}
          >
            <Stack justify="center" align="center" gap="sm">
              <Text fw={600} ta="center">
                Kopier valgt oppsett
              </Text>
              <Text size="sm" c="dimmed" ta="center">
                Kilderommet preview-kopieres til det valgte målrommet.
              </Text>
              <Divider w="100%" />
              <Text size="sm" c={copyDisabled ? "red.7" : "dimmed"} ta="center">
                {copyHint}
              </Text>
              <Button
                size="md"
                fullWidth
                rightSection={<FaArrowRight size={14} />}
                disabled={copyDisabled}
                onClick={() => dispatch(thunkCopyDevicesToSingleSelectedTargetRoom())}
              >
                Kopier til målrom
              </Button>
            </Stack>
          </Paper>

          <Paper withBorder radius="lg" p="md" shadow="xs" style={{ flex: 1, minWidth: 0 }}>
            <Stack gap="xs" h="100%">
              <Group justify="space-between" align="flex-start" wrap="wrap" gap="xs">
                <Stack gap={2}>
                  <Text fw={600}>Målrom</Text>
                  <Text size="sm" c="dimmed">
                    Rommet som er valgt i tabellen
                  </Text>
                </Stack>
                <AdvancedCopyDevicesSimilarityBadge
                  similarityScore={selectedTargetTableRow?.similarityScore ?? null}
                />
              </Group>
              <Group gap="xs" wrap="wrap">
                <Badge variant="light" color="gray">
                  Romnavn: {selectedTargetMcbimSpace?.longName || "Mangler romnavn"}
                </Badge>
                <Badge variant="light" color="gray">
                  Romsnummer: {selectedTargetMcbimSpace?.name || "Mangler romsnummer"}
                </Badge>
                <Badge variant="light" color="gray">
                  Standardrom: {selectedTargetRoom?.rfpMalRom || "-"}
                </Badge>
                {selectedTargetTableRow?.hasExistingDevices && (
                  <Badge variant="light" color="red">
                    Har komponenter fra før
                  </Badge>
                )}
                {selectedTargetRoomAlreadyCopied && (
                  <Badge variant="light" color="blue">
                    Kopiert i denne økten
                  </Badge>
                )}
              </Group>
              {selectedTargetRoomAlreadyCopied && (
                <Group justify="flex-end">
                  <Button
                    variant="light"
                    color="red"
                    leftSection={<FaTrash size={14} />}
                    onClick={() => dispatch(thunkRemoveCopiedDevicesFromSelectedTargetRoom())}
                  >
                    Fjern kopiering
                  </Button>
                </Group>
              )}
              <AdvancedCopyDevicesTargetViewer />
            </Stack>
          </Paper>
        </Group>

        {(isLoading || isFetching) && (
          <Paper withBorder radius="md" p="sm" bg="gray.0">
            <Text size="sm" c="dimmed">
              Laster rom...
            </Text>
          </Paper>
        )}

        {isError && (
          <Alert color="red" title="Feil">
            Kunne ikke hente rom for avansert kopiering.
          </Alert>
        )}
      </Stack>
    </Modal>
  );
};
